import store from "../Component/store/Provider";
import { LOGOUT_ADMIN } from "../Component/store/admin/admin.type";
import { setToast } from "./toast";

const SIGNATURE_KEY = '__perm_sig__'

/**
 * Check if the Web Crypto API is available (secure context only).
 */
function hasCryptoSubtle() {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined'
}

// ─── Web Crypto (HTTPS / localhost) ─────────────────────────────────────────

/**
 * Derive a CryptoKey from the Firebase ID token for HMAC signing.
 */
async function deriveKey(token) {
  const enc = new TextEncoder()
  const keyMaterial = enc.encode(token)

  return crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify']
  )
}

/**
 * Convert an ArrayBuffer to a hex string.
 */
function bufToHex(buf) {
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Convert a hex string back to an ArrayBuffer.
 */
function hexToBuf(hex) {
  const bytes = new Uint8Array(hex.length / 2)

  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }

  return bytes.buffer
}

// ─── Fallback (plain HTTP dev environments) ─────────────────────────────────

/**
 * Simple hash function for non-secure contexts.
 * Uses a combination of the token + data to produce a deterministic hash.
 * Not cryptographically strong, but catches casual sessionStorage edits.
 */
function simpleFallbackHash(token, data) {
  const input = token + '::' + data
  let hash = 0
  let h2 = 0xdeadbeef

  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i)
    hash = Math.imul(hash ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }

  hash = Math.imul(hash ^ (hash >>> 16), 2246822507)
  hash = Math.imul(hash ^ (hash >>> 13), 3266489909)
  hash = (hash ^ (hash >>> 16)) >>> 0

  h2 = Math.imul(h2 ^ (h2 >>> 15), 2246822507)
  h2 = Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = (h2 ^ (h2 >>> 16)) >>> 0

  // Combine both hashes into a longer string for extra collision resistance
  return 'fb_' + hash.toString(36) + '_' + h2.toString(36)
}

// ─── Shared helpers ─────────────────────────────────────────────────────────

/**
 * Create a canonical string from loginType and permissions array.
 * Sorting ensures the same data always produces the same signature.
 */
function canonicalize(loginType, permissions) {
  const sorted = [...(permissions || [])].sort((a, b) =>
    (a.module || '').localeCompare(b.module || '')
  ).map(p => ({
    module: p.module,
    actions: [...(p.actions || [])].sort()
  }))

  return JSON.stringify({ loginType: loginType || '', permissions: sorted })
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Sign the permission data and store the signature in sessionStorage.
 * Call this ONLY after a successful login, before redirecting.
 *
 * @param {string} loginType - 'staff' | 'admin'
 * @param {Array} permissions - Raw permission array from the API
 * @returns {Promise<void>}
 */
export async function signPermissions(loginType, permissions) {
  try {
    const token = sessionStorage.getItem('token')

    if (!token) {
      console.warn('[PermIntegrity] No token found – skipping signing')
      return
    }

    const data = canonicalize(loginType, permissions)

    if (hasCryptoSubtle()) {
      // Secure context — use Web Crypto HMAC-SHA256
      const key = await deriveKey(token)
      const enc = new TextEncoder()
      const signature = await crypto.subtle.sign('HMAC', key, enc.encode(data))

      sessionStorage.setItem(SIGNATURE_KEY, bufToHex(signature))
    } else {
      // Non-secure context (plain HTTP dev) — use fallback hash
      console.warn('[PermIntegrity] crypto.subtle not available (non-HTTPS). Using fallback signing.')
      const sig = simpleFallbackHash(token, data)

      sessionStorage.setItem(SIGNATURE_KEY, sig)
    }
  } catch (err) {
    console.error('[PermIntegrity] Failed to sign permissions:', err)
  }
}

/**
 * Verify that the stored permissions have not been tampered with.
 *
 * @param {string} loginType
 * @param {Array} permissions
 * @returns {Promise<boolean>} true if valid, false if tampered or missing
 */
export async function verifyPermissions(loginType, permissions) {
  try {
    const token = sessionStorage.getItem('token')
    const storedSig = sessionStorage.getItem(SIGNATURE_KEY)

    if (!token || !storedSig) {
      // If there's no token/sig we're probably not logged in
      // — don't flag this as tampering
      return !loginType && !token // only valid if also no loginType and no token
    }

    const data = canonicalize(loginType, permissions)

    if (hasCryptoSubtle()) {
      // Secure context — verify with Web Crypto
      const key = await deriveKey(token)
      const enc = new TextEncoder()

      const isValid = await crypto.subtle.verify(
        'HMAC',
        key,
        hexToBuf(storedSig),
        enc.encode(data)
      )

      return isValid
    } else {
      // Non-secure context — verify with fallback hash
      const expectedSig = simpleFallbackHash(token, data)

      return storedSig === expectedSig
    }
  } catch (err) {
    console.error('[PermIntegrity] Verification error:', err)
    return false
  }
}

/**
 * Clear the stored signature (call on logout).
 */
export function clearPermissionSignature() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(SIGNATURE_KEY)
  }
}

let isTamperActionTriggered = false;

/**
 * Centralized Security Check: Validates session integrity
 */
export async function validateSessionIntegrity() {
  if (isTamperActionTriggered) return false;

  try {
    const token = sessionStorage.getItem("token");
    if (!token) return true; // Let standard AuthRoute handle missing tokens

    const loginType = sessionStorage.getItem("loginType");
    const permissionsStr = sessionStorage.getItem("permissions");
    let permissions = [];
    try {
      permissions = permissionsStr ? JSON.parse(permissionsStr) : [];
    } catch (e) {
      permissions = [];
    }

    const isValid = await verifyPermissions(loginType, permissions);
    
    if (!isValid) {
      if (isTamperActionTriggered) return false;
      isTamperActionTriggered = true;

      console.error("[Security] Session tampering detected. Logging out immediately.");
      setToast("error", "Session tampered. Please login again.");
      store.dispatch({ type: LOGOUT_ADMIN });
      setTimeout(() => {
        window.location.href = "/admin-login";
        setTimeout(() => { isTamperActionTriggered = false; }, 1000);
      }, 500);
      return false;
    }

    // Optional Hardening: Cross-check with Redux
    const reduxState = store.getState();
    if (reduxState && reduxState.admin && reduxState.admin.isAuth) {
      const reduxPerms = reduxState.admin.permissions || [];
      const reduxLoginType = reduxState.admin.loginType;
      
      // If they don't match, it means sessionStorage was modified after Redux was loaded
      if (loginType !== reduxLoginType || JSON.stringify(reduxPerms) !== JSON.stringify(permissions)) {
        if (isTamperActionTriggered) return false;
        isTamperActionTriggered = true;

        console.error("[Security] Redux State and SessionStorage mismatch. Session tampered.");
        setToast("error", "Session tampered. Please login again.");
        store.dispatch({ type: LOGOUT_ADMIN });
        setTimeout(() => {
          window.location.href = "/admin-login";
          setTimeout(() => { isTamperActionTriggered = false; }, 1000);
        }, 500);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("[Security] Validation failed:", error);
    return false;
  }
}

export { SIGNATURE_KEY }
